import { getSession } from "next-auth/react";

// Use environment variable directly to avoid importing base.ts which has dynamic imports
// This prevents webpack from analyzing base.ts during bundling
const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL || "https://api.techxeed.com";
const MAX_CHAT_FILE_SIZE_BYTES = 10 * 1024 * 1024;
/** Browser uploads go through the Next.js proxy (Vercel ~4.5MB body limit). */
const MAX_AVATAR_SIZE_BYTES = 4 * 1024 * 1024;

type s3Response = {
	presignedUrl: string;
	s3url: string;
};

type UploadOptions = {
	entityId?: string;
	filePurpose?: string;
};

type CaptureDemoScreenshotOptions = {
	url: string;
	width: number;
	height: number;
	entityId?: string;
};

type AuthHeaderOptions = { omitContentType?: boolean };

// Helper function to get authenticated headers with retry logic
async function getAuthenticatedHeaders(
	options?: AuthHeaderOptions,
): Promise<Record<string, string>> {
	const headers: Record<string, string> = {
		"X-Requested-With": "XMLHttpRequest", // Required for CSRF protection
	};
	if (!options?.omitContentType) {
		headers["Content-Type"] = "application/json";
	}

	// Try to get session with retry logic (similar to api.ts)
	try {
		let session = null;
		for (let i = 0; i < 3; i++) {
			try {
				session = await getSession();
				if (session?.idToken) {
					break;
				}
				if (session && i < 2) {
					// Wait a bit and retry if session exists but no idToken yet
					await new Promise(resolve => setTimeout(resolve, 100));
					continue;
				}
			} catch (error) {
				if (i === 2) {
					console.error("Failed to get session for S3 upload:", error);
				} else {
					await new Promise(resolve => setTimeout(resolve, 100));
				}
			}
		}

		if (session?.idToken && session.idToken.trim().length > 0) {
			headers["Authorization"] = `Bearer ${session.idToken}`;
			console.log("S3 upload: Authorization header added");
		} else {
			console.warn("S3 upload: No session token available. Request may fail with 401/403.");
		}
	} catch (error) {
		console.error("Error getting session for S3 upload:", error);
		// Continue without auth header - request will fail with proper error message
	}

	return headers;
}

export const uploadImage = async (fileURI: string): Promise<string> => {
	// SECURITY: Fetch and validate file blob FIRST before requesting presigned URL
	// This ensures we can validate file size even if the initial fetch fails
	// We'll reuse this blob for the actual upload to avoid duplicate fetches
	let fileBlob: Blob;
	let fileSize: number = 0;
	
	if (fileURI.startsWith("data:")) {
		// Data URL - convert directly to blob and calculate size
		try {
		const base64Data = fileURI.split(',')[1];
			if (!base64Data) {
				throw new Error("Invalid data URL format: missing base64 data");
			}
			const mimeType = fileURI.match(/data:([^;]+);/)?.[1] || 'image/png';
			const byteCharacters = atob(base64Data);
			const byteNumbers = new Array(byteCharacters.length);
			for (let i = 0; i < byteCharacters.length; i++) {
				byteNumbers[i] = byteCharacters.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			fileBlob = new Blob([byteArray], { type: mimeType });
			fileSize = fileBlob.size;
		} catch (error) {
			throw new Error(`Failed to convert data URL to blob: ${error instanceof Error ? error.message : String(error)}`);
		}
	} else if (fileURI.startsWith("blob:")) {
		// Blob URL - fetch once and validate size immediately
		// SECURITY: If fetch fails, we MUST throw error - cannot proceed without size validation
		try {
			const response = await fetch(fileURI);
			if (!response.ok) {
				throw new Error(`Failed to fetch blob URL: ${response.status}. Blob URL may have expired. Please select the image again.`);
			}
			fileBlob = await response.blob();
			fileSize = fileBlob.size;
		} catch (error) {
			// SECURITY: Fail securely - if we can't get the blob, we can't validate size
			// This prevents bypassing size validation through network errors
			throw new Error(`Failed to fetch file from blob URL: ${error instanceof Error ? error.message : String(error)}. Blob URLs can expire. Please select the image again.`);
		}
	} else if (fileURI.startsWith("http://") || fileURI.startsWith("https://")) {
		// Regular HTTP/HTTPS URL - fetch and validate size
		try {
			const fileResponse = await fetch(fileURI, {
				mode: "cors",
				credentials: "omit", // Don't send credentials for external URLs
			});
			
			if (!fileResponse.ok) {
				throw new Error(`Failed to fetch image from URL: ${fileResponse.status} ${fileResponse.statusText}`);
			}
			
			fileBlob = await fileResponse.blob();
			fileSize = fileBlob.size;
		} catch (error) {
			throw new Error(`Failed to fetch file from URL: ${error instanceof Error ? error.message : String(error)}`);
		}
	} else {
		throw new Error(`Unsupported file URI format: ${fileURI.substring(0, 50)}`);
	}

	// SECURITY: Validate file size for avatar uploads (max 5MB)
	// This validation MUST happen after successfully fetching the blob
	// If fileSize is 0 or undefined at this point, something went wrong
	if (!fileSize || fileSize <= 0) {
		throw new Error("Unable to determine file size. Please select the image again.");
	}

		const MAX_AVATAR_SIZE = MAX_AVATAR_SIZE_BYTES;
		if (fileSize > MAX_AVATAR_SIZE) {
			throw new Error(`Avatar file size (${(fileSize / 1024 / 1024).toFixed(2)}MB) exceeds the maximum limit of 4MB. Please select a smaller image.`);
	}

	const body = {
		fileName: "avatar.jpeg",
		domain: "USER_PROFILE",
		contentType: "image/jpeg",
		fileSize: fileSize, // Always include file size for backend validation
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	let response: Response;
	try {
		response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
			method: "POST",
			body: JSON.stringify(body),
			headers,
			// No credentials needed - using Bearer token authentication
		});
	} catch (fetchError: any) {
		// Network error (CORS, connection refused, etc.)
		const errorMessage = fetchError?.message || "Unknown network error";
		if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError") || errorMessage.includes("Network request failed")) {
			throw new Error("Network error: Unable to connect. Please check your internet connection and try again.");
		}
		throw new Error("Upload failed. Please try again.");
	}

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	// File blob was already fetched and validated above
	// Reuse it for the upload to avoid duplicate fetches

	let uploadResponse: Response;
	try {
		uploadResponse = await fetch(result.presignedUrl, {
			method: "PUT",
			body: fileBlob,
			headers: {
				"Content-Type": body.contentType,
			},
		});
	} catch (uploadError: any) {
		// Network error during S3 upload
		const errorMessage = uploadError?.message || "Unknown network error";
		if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError") || errorMessage.includes("Network request failed")) {
			throw new Error("Network error: Unable to upload. Please check your internet connection and try again.");
		}
		throw new Error("Upload failed. Please try again.");
	}

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadFile = async (
	file: File,
	options?: UploadOptions
): Promise<string> => {
	// Validate file size for avatar uploads (max 4MB via same-origin proxy)
	if (options?.filePurpose === 'avatar') {
		if (file.size > MAX_AVATAR_SIZE_BYTES) {
			throw new Error(`Avatar file size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the maximum limit of 4MB. Please select a smaller image.`);
		}
		if (file.type && !file.type.startsWith("image/")) {
			throw new Error("Please select a valid image file (JPG, PNG, WebP, or HEIC).");
		}
		return uploadAvatarViaApi(file, options.entityId);
	}

	const body = {
		fileName: file.name,
		domain: "USER_PROFILE",
		contentType: `${file.type.toLowerCase() || "image/png"}`,
		...(options?.entityId && { entityId: options.entityId }),
		...(options?.filePurpose && { filePurpose: options.filePurpose }),
		// Include file size for backend validation (especially important for avatars)
		fileSize: file.size,
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	let response: Response;
	try {
		response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
			method: "POST",
			body: JSON.stringify(body),
			headers,
			// No credentials needed - using Bearer token authentication
		});
	} catch (fetchError: any) {
		// Network error (CORS, connection refused, etc.)
		const errorMessage = fetchError?.message || "Unknown network error";
		if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError") || errorMessage.includes("Network request failed")) {
			throw new Error("Network error: Unable to connect. Please check your internet connection and try again.");
		}
		throw new Error("Upload failed. Please try again.");
	}

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	const fileBlob = new Blob([file], { type: file.type });

	let uploadResponse: Response;
	try {
		uploadResponse = await fetch(result.presignedUrl, {
			method: "PUT",
			body: fileBlob,
		});
	} catch (uploadError: any) {
		// Network error during S3 upload
		const errorMessage = uploadError?.message || "Unknown network error";
		if (errorMessage.includes("Failed to fetch") || errorMessage.includes("NetworkError") || errorMessage.includes("Network request failed")) {
			throw new Error("Network error: Unable to upload. Please check your internet connection and try again.");
		}
		throw new Error("Upload failed. Please try again.");
	}

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};

/** Avatar: multipart via same-origin API proxy (avoids CORS / session issues in the browser). */
async function uploadAvatarViaApi(file: File, entityId?: string): Promise<string> {
	const form = new FormData();
	form.append('file', file, file.name);
	if (entityId) {
		form.append('entityId', entityId);
	}

	const headers: Record<string, string> = {
		'X-Requested-With': 'XMLHttpRequest',
	};

	const authHeaders = await getAuthenticatedHeaders({ omitContentType: true });
	Object.assign(headers, authHeaders);

	let response: Response;
	try {
		// Same-origin proxy — browser never calls api.techxeed.com directly (no CORS).
		response = await fetch('/api/s3/upload-avatar', {
			method: 'POST',
			body: form,
			headers,
			credentials: 'include',
		});
	} catch (fetchError: unknown) {
		const errorMessage =
			fetchError instanceof Error ? fetchError.message : 'Unknown network error';
		if (
			errorMessage.includes('Failed to fetch') ||
			errorMessage.includes('NetworkError') ||
			errorMessage.includes('Network request failed')
		) {
			throw new Error(
				'Network error: Unable to upload profile photo. Please check your connection and try again.',
			);
		}
		throw new Error('Profile photo upload failed. Please try again.');
	}

	const raw = await response.text();
	let result: { s3url?: string; message?: string } = {};
	try {
		result = raw ? JSON.parse(raw) : {};
	} catch {
		result = { message: raw || 'Upload failed' };
	}

	if (!response.ok) {
		throw new Error(
			result.message ||
				(response.status === 401
					? 'Your session has expired. Please sign in again and retry.'
					: 'Profile photo upload failed. Please try again.'),
		);
	}

	if (!result?.s3url) {
		throw new Error('Upload failed. Please try again.');
	}

	return result.s3url;
}

export type ServiceMediaUploadResult = {
	s3url: string;
	displayUrl: string;
};

/** Production uses presigned PUT (WAF blocks multipart >8KB). Localhost uses the API proxy. */
function usePresignedServiceMediaUpload(): boolean {
	if (typeof window === "undefined") {
		return true;
	}
	const host = window.location.hostname.toLowerCase();
	return host === "techxeed.com" || host.endsWith(".techxeed.com");
}

async function parseUploadErrorResponse(response: Response, fallback: string): Promise<string> {
	let message = fallback;
	try {
		const errBody = (await response.json()) as { message?: string };
		if (errBody?.message) message = errBody.message;
	} catch {
		const errorText = await response.text().catch(() => "");
		if (errorText && !errorText.trimStart().startsWith("<")) message = errorText;
	}
	if (response.status === 401) {
		return "Your session has expired. Please sign in again and retry.";
	}
	return message;
}

async function uploadServiceFileViaProxy(
	file: File,
	options?: UploadOptions,
): Promise<ServiceMediaUploadResult> {
	const form = new FormData();
	form.append("file", file, file.name);
	if (options?.entityId) {
		form.append("entityId", options.entityId);
	}
	if (options?.filePurpose) {
		form.append("filePurpose", options.filePurpose);
	}

	const headers: Record<string, string> = {
		"X-Requested-With": "XMLHttpRequest",
	};

	const authHeaders = await getAuthenticatedHeaders({ omitContentType: true });
	Object.assign(headers, authHeaders);

	let response: Response;
	try {
		response = await fetch("/api/s3/upload-service-media", {
			method: "POST",
			body: form,
			headers,
			credentials: "include",
		});
	} catch (fetchError: unknown) {
		const rawMsg =
			fetchError instanceof Error ? fetchError.message : String(fetchError ?? "Unknown network error");
		if (
			rawMsg.includes("Failed to fetch") ||
			rawMsg.includes("NetworkError") ||
			rawMsg.includes("Network request failed")
		) {
			throw new Error(
				"Network error: Unable to upload media. Please check your connection and try again.",
			);
		}
		throw new Error(`Service media upload failed: ${rawMsg}`);
	}

	if (!response.ok) {
		throw new Error(await parseUploadErrorResponse(response, "Service media upload failed. Please try again."));
	}

	const result = (await response.json()) as ServiceMediaUploadResult;
	if (!result?.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	return {
		s3url: result.s3url,
		displayUrl: result.displayUrl?.trim() || result.s3url,
	};
}

/**
 * Presigned flow: tiny JSON to API (WAF-safe), file PUT direct to S3, then display variant.
 */
async function uploadServiceFileViaPresigned(
	file: File,
	options?: UploadOptions,
): Promise<ServiceMediaUploadResult> {
	const contentType = file.type.toLowerCase() || "image/png";
	const filePurpose = options?.filePurpose ?? "gallery";
	const body = {
		fileName: file.name,
		domain: "SERVICE_PACKAGES",
		contentType,
		filePurpose,
		fileSize: file.size,
		...(options?.entityId ? { entityId: options.entityId } : {}),
	};

	const headers = await getAuthenticatedHeaders();

	let presignResponse: Response;
	try {
		presignResponse = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
			method: "POST",
			body: JSON.stringify(body),
			headers,
			credentials: "include",
		});
	} catch (fetchError: unknown) {
		const rawMsg =
			fetchError instanceof Error ? fetchError.message : String(fetchError ?? "Unknown network error");
		if (
			rawMsg.includes("Failed to fetch") ||
			rawMsg.includes("NetworkError") ||
			rawMsg.includes("Network request failed")
		) {
			throw new Error(
				"Network error: Unable to upload media. Please check your connection and try again.",
			);
		}
		throw new Error(`Service media upload failed: ${rawMsg}`);
	}

	if (!presignResponse.ok) {
		throw new Error(
			await parseUploadErrorResponse(presignResponse, "Service media upload failed. Please try again."),
		);
	}

	const presigned = (await presignResponse.json()) as s3Response;
	if (!presigned?.presignedUrl || !presigned?.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	let uploadResponse: Response;
	try {
		uploadResponse = await fetch(presigned.presignedUrl, {
			method: "PUT",
			body: file,
			headers: { "Content-Type": contentType },
		});
	} catch (uploadError: unknown) {
		console.warn("Direct S3 PUT failed, falling back to upload proxy:", uploadError);
		return uploadServiceFileViaProxy(file, options);
	}

	if (!uploadResponse.ok) {
		return uploadServiceFileViaProxy(file, options);
	}

	if (!contentType.startsWith("image/")) {
		return { s3url: presigned.s3url, displayUrl: presigned.s3url };
	}

	let displayResponse: Response;
	try {
		displayResponse = await fetch(`${BACKEND_URL}/s3/ensure-display-url`, {
			method: "POST",
			body: JSON.stringify({
				s3url: presigned.s3url,
				domain: "SERVICE_PACKAGES",
				filePurpose,
				...(options?.entityId ? { entityId: options.entityId } : {}),
			}),
			headers,
			credentials: "include",
		});
	} catch (displayError: unknown) {
		const rawMsg =
			displayError instanceof Error ? displayError.message : String(displayError ?? "Unknown network error");
		throw new Error(`Upload succeeded but preview generation failed: ${rawMsg}`);
	}

	if (!displayResponse.ok) {
		// Full image is in S3; listing variant can be generated on save.
		return { s3url: presigned.s3url, displayUrl: presigned.s3url };
	}

	const display = (await displayResponse.json()) as { displayUrl?: string };
	return {
		s3url: presigned.s3url,
		displayUrl: display.displayUrl?.trim() || presigned.s3url,
	};
}

/**
 * Service gallery / thumbnail / demo video upload.
 * Production: presigned PUT (CloudFront WAF blocks multipart bodies >8KB).
 * Local dev: same-origin proxy to Nest (avoids S3 bucket CORS on localhost).
 */
export const uploadServiceFile = async (
	file: File,
	options?: UploadOptions,
): Promise<ServiceMediaUploadResult> => {
	if (usePresignedServiceMediaUpload()) {
		return uploadServiceFileViaPresigned(file, options);
	}
	return uploadServiceFileViaProxy(file, options);
};

export type BlogMediaUploadResult = {
	s3url: string;
	displayUrl: string;
};

async function uploadBlogMediaFileViaProxy(
	file: File,
	options?: UploadOptions,
): Promise<BlogMediaUploadResult> {
	const form = new FormData();
	form.append("file", file, file.name);
	if (options?.entityId) {
		form.append("entityId", options.entityId);
	}
	if (options?.filePurpose) {
		form.append("filePurpose", options.filePurpose);
	}

	const headers = await getAuthenticatedHeaders({ omitContentType: true });
	const response = await fetch("/api/s3/upload-blog-media", {
		method: "POST",
		body: form,
		headers,
		credentials: "include",
	});

	if (!response.ok) {
		throw new Error(
			await parseUploadErrorResponse(response, "Image upload failed. Please try again."),
		);
	}

	const result = (await response.json()) as BlogMediaUploadResult;
	if (!result?.s3url) {
		throw new Error("Upload failed. Please try again.");
	}
	return {
		s3url: result.s3url,
		displayUrl: result.displayUrl?.trim() || result.s3url,
	};
}

/**
 * Production: tiny JSON presign (WAF-safe) then PUT the file to S3.
 * Local: multipart straight to Nest.
 */
async function uploadBlogMediaFileViaPresigned(
	file: File,
	options?: UploadOptions,
): Promise<BlogMediaUploadResult> {
	const contentType = file.type.toLowerCase() || "image/png";
	const filePurpose = options?.filePurpose ?? "content";
	const headers = await getAuthenticatedHeaders();
	const body = {
		fileName: file.name,
		domain: "BLOG",
		contentType,
		filePurpose,
		fileSize: file.size,
		...(options?.entityId ? { entityId: options.entityId } : {}),
	};

	let presignResponse: Response;
	try {
		presignResponse = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
			method: "POST",
			body: JSON.stringify(body),
			headers,
			credentials: "include",
		});
	} catch (fetchError: unknown) {
		const rawMsg =
			fetchError instanceof Error ? fetchError.message : String(fetchError ?? "Unknown network error");
		if (
			rawMsg.includes("Failed to fetch") ||
			rawMsg.includes("NetworkError") ||
			rawMsg.includes("Network request failed")
		) {
			throw new Error(
				"Network error: Unable to upload image. Please check your connection and try again.",
			);
		}
		throw new Error(`Image upload failed: ${rawMsg}`);
	}

	if (!presignResponse.ok) {
		throw new Error(
			await parseUploadErrorResponse(presignResponse, "Image upload failed. Please try again."),
		);
	}

	const presigned = (await presignResponse.json()) as s3Response;
	if (!presigned?.presignedUrl || !presigned?.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	try {
		const uploadResponse = await fetch(presigned.presignedUrl, {
			method: "PUT",
			body: file,
			headers: { "Content-Type": contentType },
		});
		if (!uploadResponse.ok) {
			return uploadBlogMediaFileViaProxy(file, options);
		}
	} catch {
		return uploadBlogMediaFileViaProxy(file, options);
	}

	if (!contentType.startsWith("image/")) {
		return { s3url: presigned.s3url, displayUrl: presigned.s3url };
	}

	try {
		const displayResponse = await fetch(`${BACKEND_URL}/s3/ensure-display-url`, {
			method: "POST",
			body: JSON.stringify({
				s3url: presigned.s3url,
				domain: "BLOG",
				filePurpose,
				...(options?.entityId ? { entityId: options.entityId } : {}),
			}),
			headers,
			credentials: "include",
		});
		if (!displayResponse.ok) {
			return { s3url: presigned.s3url, displayUrl: presigned.s3url };
		}
		const display = (await displayResponse.json()) as { displayUrl?: string };
		return {
			s3url: presigned.s3url,
			displayUrl: display.displayUrl?.trim() || presigned.s3url,
		};
	} catch {
		return { s3url: presigned.s3url, displayUrl: presigned.s3url };
	}
}

async function uploadBlogMediaFileViaApi(
	file: File,
	options?: UploadOptions,
): Promise<BlogMediaUploadResult> {
	const form = new FormData();
	form.append("file", file, file.name);
	if (options?.entityId) {
		form.append("entityId", options.entityId);
	}
	if (options?.filePurpose) {
		form.append("filePurpose", options.filePurpose);
	}

	const headers = await getAuthenticatedHeaders({ omitContentType: true });
	const response = await fetch(`${BACKEND_URL}/s3/upload-blog-media`, {
		method: "POST",
		body: form,
		headers,
		credentials: "include",
	});

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Blog media upload failed: ${response.status} ${errorText}`);
	}

	const result = (await response.json()) as BlogMediaUploadResult;
	if (!result?.s3url) {
		throw new Error("Upload failed. Please try again.");
	}
	return {
		s3url: result.s3url,
		displayUrl: result.displayUrl?.trim() || result.s3url,
	};
}

/**
 * Blog / CRM email images.
 * Production: presigned PUT (CloudFront WAF blocks multipart bodies >8KB).
 * Local: multipart to Nest.
 */
export const uploadBlogMediaFile = async (
	file: File,
	options?: UploadOptions,
): Promise<BlogMediaUploadResult> => {
	if (usePresignedServiceMediaUpload()) {
		return uploadBlogMediaFileViaPresigned(file, options);
	}
	return uploadBlogMediaFileViaApi(file, options);
};

export const captureDemoScreenshotFromUrl = async (
	options: CaptureDemoScreenshotOptions,
): Promise<{ s3url: string; width: number; height: number }> => {
	const headers = await getAuthenticatedHeaders();
	let response: Response;
	try {
		response = await fetch(`${BACKEND_URL}/services/capture-demo-screenshot`, {
			method: "POST",
			body: JSON.stringify(options),
			headers,
			credentials: "include",
		});
	} catch (fetchError: unknown) {
		const rawMsg =
			fetchError instanceof Error ? fetchError.message : String(fetchError ?? "Unknown network error");
		const msg = rawMsg.toLowerCase().includes("failed to fetch")
			? `Network error: Unable to reach backend at ${BACKEND_URL}. Ensure backend is running and reachable.`
			: `Network error while capturing screenshot: ${rawMsg}`;
		throw new Error(msg);
	}
	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Screenshot capture failed: ${response.status} ${errorText}`);
	}
	const data = (await response.json()) as {
		s3url?: string;
		width?: number;
		height?: number;
	};
	if (!data?.s3url) {
		throw new Error("Screenshot capture failed: missing uploaded URL");
	}
	return {
		s3url: data.s3url,
		width: typeof data.width === "number" ? data.width : options.width,
		height: typeof data.height === "number" ? data.height : options.height,
	};
};

export const uploadCapturedFrameImage = async (
	imageDataUrl: string,
	entityId?: string,
): Promise<string> => {
	const headers = await getAuthenticatedHeaders();
	let response: Response;
	try {
		response = await fetch(`${BACKEND_URL}/services/upload-captured-frame`, {
			method: "POST",
			body: JSON.stringify({ imageDataUrl, ...(entityId ? { entityId } : {}) }),
			headers,
			credentials: "include",
		});
	} catch (fetchError: unknown) {
		const rawMsg =
			fetchError instanceof Error ? fetchError.message : String(fetchError ?? "Unknown network error");
		throw new Error(
			rawMsg.toLowerCase().includes("failed to fetch")
				? `Network error: Unable to reach backend at ${BACKEND_URL}. Ensure backend is running and reachable.`
				: `Network error while uploading captured frame: ${rawMsg}`,
		);
	}
	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`Captured frame upload failed: ${response.status} ${errorText}`);
	}
	const data = (await response.json()) as { s3url?: string };
	if (!data?.s3url) {
		throw new Error("Captured frame upload failed: missing uploaded URL");
	}
	return data.s3url;
};

export const uploadReviewVideo = async (
	file: File,
	entityId?: string
): Promise<string> => {
	// Validate file size (max 1GB for videos)
	const MAX_VIDEO_SIZE = 1024 * 1024 * 1024; // 1GB in bytes
	if (file.size > MAX_VIDEO_SIZE) {
		const fileSizeGB = (file.size / 1024 / 1024 / 1024).toFixed(2);
		throw new Error(
			`Video file size (${fileSizeGB}GB) exceeds the maximum limit of 1GB. Please select a smaller video.`
		);
	}

	// Validate file type
	if (!file.type.startsWith("video/")) {
		throw new Error("Please select a valid video file.");
	}

	const form = new FormData();
	form.append("file", file, file.name);
	if (entityId) {
		form.append("entityId", entityId);
	}

	const headers = await getAuthenticatedHeaders({ omitContentType: true });

	let response: Response;
	try {
		response = await fetch(`${BACKEND_URL}/s3/upload-review-video`, {
			method: "POST",
			body: form,
			headers,
		});
	} catch (fetchError: unknown) {
		const errorMessage =
			fetchError instanceof Error ? fetchError.message : "Unknown network error";
		if (
			errorMessage.includes("Failed to fetch") ||
			errorMessage.includes("NetworkError") ||
			errorMessage.includes("Network request failed")
		) {
			throw new Error(
				"Network error: Unable to upload video. Please check your internet connection and try again."
			);
		}
		throw new Error("Failed to upload video. Please try again.");
	}

	if (!response.ok) {
		throw new Error("Video upload failed. Please try again.");
	}

	const result = (await response.json()) as { s3url?: string };
	if (!result?.s3url) {
		throw new Error("Video upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadReviewVideoThumbnail = async (
	file: File,
	entityId?: string
): Promise<string> => {
	if (!file.type.startsWith("image/")) {
		throw new Error("Please select a valid thumbnail image.");
	}

	const form = new FormData();
	form.append("file", file, file.name);
	if (entityId) {
		form.append("entityId", entityId);
	}

	const headers = await getAuthenticatedHeaders({ omitContentType: true });
	const response = await fetch(`${BACKEND_URL}/s3/upload-review-thumbnail`, {
		method: "POST",
		body: form,
		headers,
	});

	if (!response.ok) {
		throw new Error("Thumbnail upload failed. Please try again.");
	}

	const result = (await response.json()) as { s3url?: string };
	if (!result?.s3url) {
		throw new Error("Thumbnail upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadChatFile = async (
	file: File,
	options?: UploadOptions
): Promise<string> => {
	if (file.size > MAX_CHAT_FILE_SIZE_BYTES) {
		throw new Error(`Chat attachment size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the maximum limit of 10MB.`);
	}

	const contentType = file.type.toLowerCase() || "image/png";
	const body = {
		fileName: file.name,
		domain: "CHAT",
		contentType,
		...(options?.entityId && { entityId: options.entityId }),
		...(options?.filePurpose && { filePurpose: options.filePurpose }),
		fileSize: file.size,
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	const response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
		method: "POST",
		body: JSON.stringify(body),
		headers,
		credentials: "include", // Required for cross-origin requests (www.techxeed.com -> api.techxeed.com)
	});

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	const fileBlob = new Blob([file], { type: contentType });

	const uploadResponse = await fetch(result.presignedUrl, {
		method: "PUT",
		body: fileBlob,
		headers: {
			"Content-Type": contentType,
		},
	});

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadOrderFile = async (
	file: File,
	options?: UploadOptions
): Promise<string> => {
	const body = {
		fileName: file.name,
		domain: "ORDER",
		contentType: `${file.type.toLowerCase() || "image/png"}`,
		...(options?.entityId && { entityId: options.entityId }),
		...(options?.filePurpose && { filePurpose: options.filePurpose }),
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	const response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
		method: "POST",
		body: JSON.stringify(body),
		headers,
		credentials: "include", // Required for cross-origin requests (www.techxeed.com -> api.techxeed.com)
	});

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	const fileBlob = new Blob([file], { type: file.type });

	const uploadResponse = await fetch(result.presignedUrl, {
		method: "PUT",
		body: fileBlob,
	});

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadBlogFile = async (
	file: File,
	options?: UploadOptions
): Promise<string> => {
	const body = {
		fileName: file.name,
		domain: "BLOG",
		contentType: `${file.type.toLowerCase() || "image/png"}`,
		...(options?.entityId && { entityId: options.entityId }),
		...(options?.filePurpose && { filePurpose: options.filePurpose }),
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	const response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
		method: "POST",
		body: JSON.stringify(body),
		headers,
		credentials: "include", // Required for cross-origin requests (www.techxeed.com -> api.techxeed.com)
	});

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	const fileBlob = new Blob([file], { type: file.type });

	const uploadResponse = await fetch(result.presignedUrl, {
		method: "PUT",
		body: fileBlob,
	});

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadCategoryFile = async (
	file: File,
	options?: UploadOptions
): Promise<string> => {
	const body = {
		fileName: file.name,
		domain: "CATEGORY",
		contentType: `${file.type.toLowerCase() || "image/png"}`,
		...(options?.entityId && { entityId: options.entityId }),
		...(options?.filePurpose && { filePurpose: options.filePurpose }),
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	const response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
		method: "POST",
		body: JSON.stringify(body),
		headers,
		credentials: "include", // Required for cross-origin requests (www.techxeed.com -> api.techxeed.com)
	});

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	const fileBlob = new Blob([file], { type: file.type });

	const uploadResponse = await fetch(result.presignedUrl, {
		method: "PUT",
		body: fileBlob,
	});

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};

export const uploadAdminAsset = async (
	file: File,
	options?: UploadOptions
): Promise<string> => {
	const body = {
		fileName: file.name,
		domain: "ADMIN_ASSETS",
		contentType: `${file.type.toLowerCase() || "image/png"}`,
		...(options?.entityId && { entityId: options.entityId }),
		...(options?.filePurpose && { filePurpose: options.filePurpose }),
	};
	const headers = await getAuthenticatedHeaders();
	
	// PERFORMANCE & SECURITY: Add credentials for cross-origin requests in production
	const response = await fetch(`${BACKEND_URL}/s3/generate-presigned-url`, {
		method: "POST",
		body: JSON.stringify(body),
		headers,
		credentials: "include", // Required for cross-origin requests (www.techxeed.com -> api.techxeed.com)
	});

	if (!response.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	const result: s3Response = await response.json();
	
	if (!result || !result.presignedUrl || !result.s3url) {
		throw new Error("Upload failed. Please try again.");
	}

	const fileBlob = new Blob([file], { type: file.type });

	const uploadResponse = await fetch(result.presignedUrl, {
		method: "PUT",
		body: fileBlob,
	});

	if (!uploadResponse.ok) {
		throw new Error("Upload failed. Please try again.");
	}

	return result.s3url;
};
